import React, { useState, useEffect } from 'react'
import { db } from '../../firebase'
import { collection, getDocs } from 'firebase/firestore'
import { FaFire, FaBook, FaGraduationCap } from 'react-icons/fa'
import LoadingSnipper from '../Common/LoadingSnipper'

function FlashcardCard({ flashcard, level }) {
    const [isFlipped, setIsFlipped] = useState(false)
    
    const getLevelGradient = () => {
        if (level === 'Intro') return 'from-purple-600 to-indigo-600'
        if (level === 'Nível 1') return 'from-green-500 to-emerald-600'
        if (level === 'Nível 2') return 'from-orange-500 to-red-500'
        if (level === 'Nível 3') return 'from-blue-500 to-cyan-500'
        return 'from-gray-600 to-gray-700'
    }

    return (
        <div 
            className="flashcard-container w-full max-w-sm mx-auto cursor-pointer"
            onClick={() => setIsFlipped(!isFlipped)}
            style={{ perspective: '1000px' }}
        >
            <div 
                className="relative h-64 w-full"
                style={{
                    transformStyle: 'preserve-3d',
                    transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                    transition: 'transform 0.6s'
                }}
            >
                {/* Front of card */}
                <div 
                    className={`absolute inset-0 rounded-xl shadow-xl p-6 flex flex-col justify-center items-center bg-gradient-to-br ${getLevelGradient()} text-white`}
                    style={{
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden'
                    }}
                >
                    <div className="text-center">
                        <FaBook className="text-4xl mb-4 mx-auto opacity-80" />
                        <h3 className="text-2xl font-bold mb-2">{flashcard.word || 'Palavra'}</h3>
                        <p className="text-lg opacity-90">Toque para ver a tradução</p>
                    </div>
                </div>
                
                {/* Back of card */}
                <div 
                    className="absolute inset-0 rounded-xl shadow-xl p-6 flex flex-col justify-center items-center bg-white border-2 border-purple-200"
                    style={{
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)'
                    }}
                >
                    <div className="text-center text-gray-800">
                        <FaGraduationCap className="text-4xl mb-4 mx-auto text-purple-600" />
                        <h3 className="text-2xl font-bold mb-2 text-purple-700">{flashcard.translation || 'Translation'}</h3>
                        <p className="text-lg text-gray-600">{flashcard.word || 'Word'}</p>
                        {flashcard.example && (
                            <p className="text-sm text-gray-500 mt-4 italic">{flashcard.example}</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

function FlashcardsSection() {
    const [flashcards, setFlashcards] = useState([])
    const [loading, setLoading] = useState(true)
    const [selectedLevel, setSelectedLevel] = useState('all')

    useEffect(() => {
        const fetchFlashcards = async () => {
            try {
                // Try to fetch from flashcards collection
                const collectionRef = collection(db, 'flashcards')
                const snapshot = await getDocs(collectionRef)
                
                if (!snapshot.empty) {
                    const flashcardsData = snapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }))
                    setFlashcards(flashcardsData)
                } else {
                    // If no flashcards exist, use sample data
                    setFlashcards([])
                }
            } catch (error) {
                console.error('Error fetching flashcards:', error)
                setFlashcards([])
            } finally {
                setLoading(false)
            }
        }
        
        fetchFlashcards()
    }, [])

    if (loading) {
        return <LoadingSnipper />
    }

    const levels = ['all', 'Intro', 'Nível 1', 'Nível 2', 'Nível 3']
    const filteredFlashcards = selectedLevel === 'all' 
        ? flashcards 
        : flashcards.filter(fc => fc.level === selectedLevel)

    // Sample flashcards if none exist
    const sampleFlashcards = flashcards.length === 0 ? [
        { id: 1, word: 'Hello', translation: 'Olá', level: 'Intro', example: 'Hello, how are you?' },
        { id: 2, word: 'Goodbye', translation: 'Tchau', level: 'Intro', example: 'Goodbye, see you later!' },
        { id: 3, word: 'Thank you', translation: 'Obrigado', level: 'Intro', example: 'Thank you very much!' },
    ] : []

    const displayFlashcards = flashcards.length > 0 ? filteredFlashcards : sampleFlashcards

    return (
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="flex items-center justify-center gap-3 mb-4">
                    <FaFire className="text-3xl text-orange-500" />
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                        Flashcards
                    </h2>
                </div>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                    Pratique vocabulário de forma interativa. Toque nas cartas para ver as traduções!
                </p>
            </div>

            {/* Level Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
                {levels.map(level => (
                    <button
                        key={level}
                        onClick={() => setSelectedLevel(level)}
                        className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                            selectedLevel === level
                                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg scale-105'
                                : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'
                        }`}
                    >
                        {level === 'all' ? 'Todos' : level}
                    </button>
                ))}
            </div>

            {/* Flashcards Grid */}
            {displayFlashcards.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    {displayFlashcards.map((flashcard) => (
                        <FlashcardCard 
                            key={flashcard.id} 
                            flashcard={flashcard}
                            level={flashcard.level || 'Intro'}
                        />
                    ))}
                </div>
            ) : (
                <div className="text-center py-12 bg-white/70 backdrop-blur-sm rounded-xl shadow-lg border border-white/50">
                    <FaBook className="text-5xl text-gray-400 mx-auto mb-4" />
                    <p className="text-xl text-gray-600 mb-2">Nenhum flashcard disponível</p>
                    <p className="text-gray-500">Os flashcards serão adicionados em breve!</p>
                </div>
            )}

            {/* Coming Soon Features */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-purple-100 text-center">
                    <div className="text-3xl mb-3">📚</div>
                    <h3 className="font-bold text-lg mb-2">Decks Personalizados</h3>
                    <p className="text-sm text-gray-600">Crie seus próprios decks de flashcards</p>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-purple-100 text-center">
                    <div className="text-3xl mb-3">📊</div>
                    <h3 className="font-bold text-lg mb-2">Progresso</h3>
                    <p className="text-sm text-gray-600">Acompanhe seu progresso de aprendizado</p>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-purple-100 text-center">
                    <div className="text-3xl mb-3">🎯</div>
                    <h3 className="font-bold text-lg mb-2">Repetição Espaçada</h3>
                    <p className="text-sm text-gray-600">Sistema inteligente de revisão</p>
                </div>
            </div>
        </div>
    )
}

export default FlashcardsSection

